import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BaseInputComponent } from './base-input/base-input.component';
import { BLOCKED_PASSWORDS } from 'app/constants/auth-constants';
import { SvgLoaderComponent } from "../images/svg-loader/svg-loader.component";

@Component({
  selector: 'app-password-strength-input',
  standalone: true,
  imports: [CommonModule, BaseInputComponent, SvgLoaderComponent],
  templateUrl: './password-strength-input.component.html',
  styleUrl: './password-strength-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PasswordStrengthInputComponent),
      multi: true,
    },
  ],
})
export class PasswordStrengthInputComponent implements ControlValueAccessor {
  @Input() id?: string;
  @Input() name?: string;
  @Input() placeholder = '';
  @Input() value = '';
  @Input() autocomplete?: string;
  @Input() ariaLabel?: string;
  @Input() maxlength?: number;
  @Input() isFluid = false;
  @Input() disabled = false;
  @Input() mt = '';
  @Input() mb = '';

  @Output() focusEvent = new EventEmitter<FocusEvent>();
  @Output() blurEvent = new EventEmitter<FocusEvent>();
  @Output() valueChange = new EventEmitter<string>();
  @Output() onEnter = new EventEmitter<void>();

  showPassword = false;
  strength = 0;
  strengthLabel = '';

  private onChange: (v: any) => void = () => {};
  private onTouched: () => void = () => {};

  onInput(value: string) {
    this.value = value;
    this.onChange(value);
    this.valueChange.emit(value);
    this.evaluateStrength(value);
  }

  togglePassword() {
    this.showPassword = !this.showPassword;
  }

  handleFocus(event: FocusEvent) {
    this.focusEvent.emit(event);
  }

  handleBlur(event: FocusEvent) {
    this.onTouched();
    this.blurEvent.emit(event);
  }

  writeValue(obj: any): void {
    this.value = obj ?? '';
    this.evaluateStrength(this.value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private evaluateStrength(password: string) {
    const lower = password.toLowerCase();
    const hasNumber = /\d/.test(password);
    const hasSymbol = /[^A-Za-z0-9]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const isBlocked = BLOCKED_PASSWORDS.includes(lower);

    if (!password) {
      this.strength = 0;
      this.strengthLabel = '';
    } else if (password.length < 8 || isBlocked) {
      this.strength = 1;
      this.strengthLabel = 'Weak - Add Characters';
    } else if (password.length >= 10 && hasUpper && hasNumber && hasSymbol) {
      this.strength = 3;
      this.strengthLabel = 'Strong';
    } else if (password.length >= 8 && (hasNumber || hasSymbol)) {
      this.strength = 2;
      this.strengthLabel = 'Moderate';
    } else {
      this.strength = 1;
      this.strengthLabel = 'Weak - Add Symbols & Numbers';
    }
  }
}
